<template>
  <div class="style-quiz-back-wrapper">
    <div class="style-quiz-back">
      <button class="prev" @click="exitStyleQuiz">
        <img
          src="@/assets/icons/left.svg"
          :alt="$t('project_details.proposal.slider_arrow_left_alt')"
        />
        <span>{{ $t("style_quiz.go_back") }}</span>
      </button>
    </div>
  </div>

  <create-project-steps-menu :current-step="2"></create-project-steps-menu>

  <div class="quiz-rooms loader-block-parent">
    <div v-show="isLoading" class="loader-block">
      <div>
        <div class="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </div>
    <p class="client-selection__heading">
      {{ $t("style_quiz.rooms.select_room") }}
    </p>
    <p class="client-selection__subheading">
      {{ $t("style_quiz.rooms.select_room_desc") }}
    </p>
    <div class="quiz-rooms__items">
      <div
        class="room"
        v-for="room in rooms"
        @click.prevent="toggleRoom(room.id)"
        :class="{ active: selectedRoomsIds.includes(room.id) }"
      >
        <div
          class="indicator"
          :class="{
            checked: selectedRoomsIds.includes(room.id),
          }"
        ></div>
        <div
          class="room__image"
          :style="'background-image: url(' + room.imagePath + ')'"
        ></div>
        <div class="room__name">{{ room.name_pl ?? room.name }}</div>
        <div class="control control--input control-number">
          <div>
            <button
              type="button"
              data-type="dec"
              @click.stop="decrementAmount(room.id)"
            >
              -
            </button>
            <input
              class="amount-input"
              placeholder="0"
              step="1"
              v-model="roomAmounts[room.id]"
              type="number"
              @click.stop=""
              @keydown="removeDecimal($event, room.id)"
            />
            <button
              type="button"
              data-type="inc"
              @click.stop="incrementAmount(room.id)"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="buttons">
    <button @click.prevent="goBack" class="back">
      {{ $t("style_quiz.rooms.prev") }}
    </button>
    <button
      class="next"
      @click.prevent="submitRooms"
      :class="{ disabled: sendingForm }"
      :disabled="sendingForm"
    >
      <template v-if="!sendingForm">
        {{ $t("style_quiz.rooms.next") }}
      </template>
      <template v-else> {{ $t("style_quiz.rooms.next") }}... </template>
    </button>
  </div>
</template>

<script>
import CreateProjectStepsMenu from "@/components/createProject/CreateProjectStepsMenu";
import { useStyleQuizStore } from "@/stores/styleQuiz.store";
import { storeToRefs } from "pinia";
import { inject, ref, watch } from "vue";
import AlertHelper from "@/helpsers/alert.helper";
import router from "@/router";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";
import { useI18n } from "vue-i18n";
export default {
  name: "CreateProjectRooms",
  components: { CreateProjectStepsMenu },
  setup() {
    const styleQuizStore = useStyleQuizStore();
    let { rooms, isLoading } = storeToRefs(styleQuizStore);
    let selectedRoomsIds = ref([]);
    let roomAmounts = ref({});
    let sendingForm = ref(false);
    let alert = inject("alert");
    const { t } = useI18n();
    styleQuizStore.fetchRooms(router.currentRoute.value.params.type);

    function removeDecimal(e, roomId) {
      const key = e.key;

      if (key === "." || key === ",") return e.preventDefault();
    }

    async function fetchClientRooms() {
      isLoading.value = true;
      axios
        .get(
          ApiHelper.getProjectDetailsUrl(router.currentRoute.value.params.id),
          {
            headers: authHelper.authHeader(),
          }
        )
        .then((result) => {
          let clientRooms = result?.data?.project_rooms ?? {};
          for (let clientRoomIndex in clientRooms) {
            if (
              selectedRoomsIds.value.includes(
                clientRooms[clientRoomIndex].roomId
              )
            ) {
              roomAmounts.value[clientRooms[clientRoomIndex].roomId]++;
              continue;
            }
            selectedRoomsIds.value.push(clientRooms[clientRoomIndex].roomId);
            roomAmounts.value[clientRooms[clientRoomIndex].roomId] =
              clientRooms[clientRoomIndex].amount;
          }
          isLoading.value = false;
        })
        .catch((errorResponse) => {
          if (errorResponse?.response?.data?.code == "404")
            router.push({ path: "/not-found" });
          isLoading.value = false;
        });
    }
    fetchClientRooms();

    function toggleRoom(roomId) {
      if (selectedRoomsIds.value.includes(roomId)) {
        selectedRoomsIds.value = selectedRoomsIds.value.filter(
          (f) => f !== roomId
        );
      } else {
        selectedRoomsIds.value.push(roomId);
        if (!roomAmounts.value[roomId]) roomAmounts.value[roomId] = 1;
      }
    }

    function decrementAmount(roomId) {
      if (roomAmounts.value[roomId] && roomAmounts.value[roomId] > 0)
        roomAmounts.value[roomId] -= 1;
    }

    function incrementAmount(roomId) {
      if (roomAmounts.value[roomId]) roomAmounts.value[roomId] += 1;
      else roomAmounts.value[roomId] = 1;
    }

    function goBack() {
      router.back();
    }

    function mapRoomsForRequest() {
      let roomsWithAmount = [];
      for (let selectedRoomIndex in selectedRoomsIds.value) {
        let currentRoomId = selectedRoomsIds.value[selectedRoomIndex];
        roomsWithAmount.push({
          id: currentRoomId,
          amount: roomAmounts.value[currentRoomId] ?? 1,
        });
      }

      return roomsWithAmount;
    }

    const submitRooms = async () => {
      if (selectedRoomsIds.value.length < 1) {
        AlertHelper.show(alert, t("style_quiz.rooms.select_room"), "", "info");
      } else {
        sendingForm.value = true;
        const data = {
          rooms: mapRoomsForRequest(),
        };

        axios
          .post(
            ApiHelper.addManyRoomsToProjectUrl(
              router.currentRoute.value.params.id
            ),
            data,
            {
              headers: authHelper.authHeader(),
            }
          )
          .then((result) => {
            sendingForm.value = false;
            router.push({
              name: "create-project-furnitures",
              params: { id: router.currentRoute.value.params.id },
            });
          })
          .catch(({ response }) => {
            sendingForm.value = false;
            let errorMessage = ApiHelper.getResponseMessage(
              response?.data?.code
            );
            AlertHelper.show(alert, errorMessage, "", "error");
          });
      }
    };

    function exitStyleQuiz() {
      router.push({
        name: "project-details",
        params: {
          id: router.currentRoute.value.params.id,
          activeTab: "styleQuiz",
        },
      });
    }

    return {
      rooms,
      isLoading,
      selectedRoomsIds,
      roomAmounts,
      incrementAmount,
      decrementAmount,
      toggleRoom,
      goBack,
      submitRooms,
      sendingForm,
      exitStyleQuiz,
      removeDecimal,
    };
  },
};
</script>

<style scoped lang="scss">
@use "@/css/quiz";
@use "@/css/custom-quiz";

.amount-input {
  text-align: center;
  padding-left: 0;

  &::placeholder {
    color: #000;
  }
}

.room {
  &.active {
    .indicator {
      &.checked {
        background-color: #f4f3f3;
        border: 0;
        background-image: url("./../../assets/icons/checkmark-dark.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: 70%;
      }
    }
  }
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>